import React, {useState, useCallback} from 'react'
import {SIDEBAR_ITEM_IDS} from '../../../constants/sidebarConfig'
import {useSidebar} from '../../hooks/useSidebar'
import {useAddImageApply} from '../../hooks/useAddImageApply'
import {getDesignLibraryImage} from '../../../../../services/ArtworkTemplateServices'
import {toaster} from '../../../../../shared/PFToast'
import NodesPanel from './layers/NodesPanel'
import EditorDevToolsPanel from './devtools/EditorDevToolsPanel'
import ShapesPanel from './shapes/ShapesPanel'
import AddImageModal from './add-image/AddImageModal'
import Icon from '../../../../common/Icon'

function getLibraryImageUrl(data) {
    return data?.origin_url || data?.link || ''
}

async function fetchImageBlob(url) {
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`)
    }

    return response.blob()
}

function getFileExtensionFromType(type) {
    const mimeTypeToExtension = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp',
        'image/gif': 'gif',
        'image/svg+xml': 'svg',
        'image/bmp': 'bmp',
    }

    return mimeTypeToExtension[type] || 'png'
}

function getFileNameFromUrl(url, blob) {
    try {
        const {pathname} = new URL(url, window.location.origin)
        const rawFileName = pathname.split('/').pop()
        if (rawFileName && rawFileName.includes('.')) return rawFileName
    } catch (error) {
        console.error('Failed to parse image URL for filename', error)
    }

    return `library-image.${getFileExtensionFromType(blob?.type)}`
}

function createImageFile(blob, url) {
    return new File([blob], getFileNameFromUrl(url, blob), {
        type: blob?.type || 'image/png',
        lastModified: Date.now(),
    })
}

async function createImageFileFromUrl(url) {
    if (!url) throw new Error('Image URL is required')

    const blob = await fetchImageBlob(url)
    return createImageFile(blob, url)
}

const Sidebar = ({
    runtime,
    state,
    dispatch,
    currentDraftId,
    onSelectDraft,
    onSelectShape,
    onOpenRemoveBg,
    savedShapesRefreshKey,
}) => {
    const [showAddImageModal, setShowAddImageModal] = useState(false)

    const {items, activeItem, onItemClick} = useSidebar(runtime, {
        onAddImage: useCallback(() => setShowAddImageModal(true), []),
    })

    // --- Add-image orchestration ---
    const {handlePickFile} = useAddImageApply({runtime, draftId: currentDraftId})

    const handleSelectLibraryImage = useCallback(
        async (imageId) => {
            try {
                const {success, data, message} = await getDesignLibraryImage(imageId)
                if (!success) throw new Error(message)
                const url = getLibraryImageUrl(data)
                const file = await createImageFileFromUrl(url)
                handlePickFile(file)
            } catch (err) {
                toaster({type: 'error', message: err?.message || 'Failed to load image'})
            }
        },
        [handlePickFile],
    )

    return (
        <div className="CreateCustomShape__SidebarWrap">
            <aside className="CreateCustomShape__Toolbar" aria-label="Editor tools">
                <div className="CreateCustomShape__ToolbarSection">
                    {items.map((item) => {
                        const isActive = item.panelId != null && activeItem === item.panelId
                        const ItemIcon = item.icon
                        return (
                            <button
                                key={item.id}
                                type="button"
                                className={`CreateCustomShape__ToolbarItem ${isActive ? 'is-active' : ''}`}
                                onClick={() => onItemClick(item)}
                                aria-label={item.label}
                                aria-pressed={item.panelId != null ? isActive : undefined}
                            >
                                {ItemIcon && (
                                    <span className="CreateCustomShape__ToolbarIcon">
                                        <Icon icon={ItemIcon} />
                                    </span>
                                )}
                                <span className="CreateCustomShape__ToolbarLabel">{item.label}</span>
                            </button>
                        )
                    })}
                </div>
            </aside>

            {activeItem === SIDEBAR_ITEM_IDS.LAYERS && (
                <NodesPanel state={state} dispatch={dispatch} onOpenRemoveBg={onOpenRemoveBg} />
            )}
            {activeItem === SIDEBAR_ITEM_IDS.DEVTOOLS && runtime?.devTools && (
                <EditorDevToolsPanel devTools={runtime.devTools} runtime={runtime} />
            )}
            {activeItem === SIDEBAR_ITEM_IDS.SHAPES && (
                <ShapesPanel
                    runtime={runtime}
                    onSelectDraft={onSelectDraft}
                    onSelectShape={onSelectShape}
                    savedShapesRefreshKey={savedShapesRefreshKey}
                />
            )}

            <AddImageModal
                isOpen={showAddImageModal}
                toggle={() => setShowAddImageModal((v) => !v)}
                onPickFile={handlePickFile}
                onSelectLibraryImage={handleSelectLibraryImage}
            />
        </div>
    )
}

export default Sidebar
